import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { recommendationService } from "../../api/recommendations";
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Share2,
  Home,
} from "lucide-react";

const SharedRecommendationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sharedVia, setSharedVia] = useState("");

  useEffect(() => {
    const fetchRecommendation = async () => {
      try {
        setLoading(true);
        const data = await recommendationService.getPublic(token);
        setRecommendation(data.recommendation);
        setSharedVia(data.shared_via);
        setError("");
      } catch (err) {
        setError(
          err.response?.data?.error ||
            "This recommendation link has expired or is invalid"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendation();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="inline-block">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-gray-600 mt-4">Loading recommendation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Link Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 w-full"
          >
            <Home size={20} />
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Card */}
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header Background */}
          <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <h1 className="text-white text-2xl font-bold">
                ✨ Job Opportunity for You
              </h1>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-8 -mt-16 relative z-10">
            {/* Intro */}
            <div className="mb-8 text-center">
              <p className="text-gray-700 mb-2">
                <strong>{recommendation?.recruiter.first_name || "A recruiter"}</strong> thinks you'd be a great fit for this opportunity:
              </p>
              {sharedVia && (
                <p className="text-sm text-gray-500">
                  Shared via {sharedVia}
                </p>
              )}
            </div>

            {/* Job Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 p-8 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {recommendation?.job.title}
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                📍 {recommendation?.job.location}
              </p>

              {recommendation?.job.job_type && (
                <p className="text-sm text-gray-600 mb-4">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {recommendation.job.job_type}
                  </span>
                </p>
              )}

              <p className="text-gray-700 text-sm leading-relaxed">
                {recommendation?.job.description?.substring(0, 300)}...
              </p>
            </div>

            {/* Message */}
            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 mb-8 rounded">
              <p className="text-sm text-amber-600 font-semibold mb-2">
                Message from recruiter:
              </p>
              <p className="text-gray-800 italic">
                "{recommendation?.message}"
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <a
                href={`${window.location.origin}/jobs/${recommendation?.job.id}`}
                className="block w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2"
              >
                <ExternalLink size={20} />
                View Full Opportunity
              </a>

              <p className="text-center text-sm text-gray-600">
                Create an account or login to apply
              </p>
            </div>

            {/* Share Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600 mb-4">
                Know someone who might be interested?
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => {
                    const text = `Check out this opportunity: ${recommendation?.job.title}\n${window.location.href}`;
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(text)}`,
                      "_blank"
                    );
                  }}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Share2 size={16} />
                  <span className="hidden sm:inline">WhatsApp</span>
                </button>

                <button
                  onClick={() => {
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
                      "_blank"
                    );
                  }}
                  className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Share2 size={16} />
                  <span className="hidden sm:inline">LinkedIn</span>
                </button>

                <button
                  onClick={() => {
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Great job opportunity: ${recommendation?.job.title} ${window.location.href}`)}`,
                      "_blank"
                    );
                  }}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Share2 size={16} />
                  <span className="hidden sm:inline">X</span>
                </button>

                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert("Link copied to clipboard!");
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Share2 size={16} />
                  <span className="hidden sm:inline">Copy</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-8 border-t border-gray-200 text-center text-xs text-gray-500">
              <p>
                This recommendation link is valid for 30 days from the date it
                was shared.
              </p>
              <p>
                Powered by{" "}
                <span className="font-semibold text-gray-700">WinguPort</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedRecommendationPage;
